<script setup lang="ts">
import { computed } from 'vue';
import type { FieldSchema } from '../../../types';
import type { GenericField } from '@Types';
import { convertToTitleCase } from '@Helpers';

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  disabled?: boolean;
  error?: string;
  field?: FieldSchema;
  options?: GenericField[];
}>();

const model = defineModel<any>();

const sanitizedOptions = computed(() => props.field?.edit?.options || props.options);
const filteredOptions = computed(() =>
  sanitizedOptions.value?.filter((opt: any) => opt !== null && opt !== undefined && isOptionSelectable(opt))
);

function extractValue(option: any) {
  return isSimpleOption(option) ? option : option.value;
}

function isSimpleOption(option: any) {
  return ['string', 'number', 'boolean'].includes(typeof option);
}

function isOptionSelectable(option: any) {
  if (isSimpleOption(option) || option.active !== false) return true;

  return extractValue(option) === extractValue(model.value);
}

function getOptionLabel(option: any) {
  return isSimpleOption(option) ? option : option.label || option.name || option.value;
}

function getOptionKey(option: any): string {
  return isSimpleOption(option) ? option : `${getOptionLabel(option)}_${option.value}`;
}
</script>

<template>
  <div
    v-if="filteredOptions.length"
    class="flex flex-wrap gap-4"
  >
    <div
      v-for="option in filteredOptions"
      class="flex items-center"
      :key="getOptionKey(option)"
    >
      <RadioButton
        :disabled="field?.state?.disabled || disabled"
        :inputId="getOptionKey(option)"
        :invalid="!!error"
        :modelValue="isSimpleOption(model) ? model : model?.value"
        :name="getOptionLabel(option)"
        :value="isSimpleOption(option) ? option : option.value"
        @update:modelValue="
          (val) => {
            const value = extractValue(option);
            model = value;
          }
        "
      />

      <label
        :class="['ml-2 text-sm', { 'af-field-error': !!error }]"
        :for="getOptionKey(option)"
      >
        {{ convertToTitleCase(getOptionLabel(option)) }}
      </label>
    </div>
  </div>

  <p
    v-if="(!filteredOptions || filteredOptions.length === 0) && field?.label"
    class="py-1 text-xs italic text-gray-500"
  >
    {{ $t('components.emptyMessage.missingAnimalField', { field: $t(field?.label || '') }) }}
  </p>
</template>
